<script setup lang="ts">
import { __ } from '@@/bits/intl'
import { tailwindFlattenedColors } from '@@/bits/tailwind_config'
import { computed, nextTick, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    size?: number
    color?: 'rainbow' | 'black' | 'white' | 'yellow' | 'purple'
    checkmarkColor?:
      | 'rainbow'
      | 'black'
      | 'white'
      | 'yellow'
      | 'purple'
      | 'transparent-with-green-bg'
      | 'transparent-with-red-bg'
      | 'transparent-with-yellow-bg'
      | 'transparent-with-purple-bg'
    checkmarkIcon?: 'checkmark' | 'error'
    variation?: 'default' | 'dashed' | 'snackbar'
    darkMode?: boolean | 'auto'
    isCheckmark?: boolean
    progress?: number | null
    spinnerLabel?: string
    checkmarkLabel?: string
    xSpinnerRail?: boolean
  }>(),
  {
    size: 48,
    color: 'white',
    checkmarkColor: undefined,
    checkmarkIcon: 'checkmark',
    variation: 'default',
    darkMode: 'auto',
    isCheckmark: false,
    progress: null,
    spinnerLabel: __('Loading'),
    checkmarkLabel: __('Done'),
    xSpinnerRail: false,
  },
)

const effectiveCheckmarkColor = computed(() => {
  if (props.checkmarkColor != null) return props.checkmarkColor
  if (props.variation === 'snackbar') {
    return props.darkMode ? 'transparent-with-yellow-bg' : 'transparent-with-purple-bg'
  }
  return props.color
})

const emit = defineEmits(['transformed-to-checkmark'])

const loaderSize = computed(() => {
  return props.size
})

const loaderStyle = computed(() => {
  if (!props.size) return undefined
  return {
    width: `${loaderSize.value}px`,
    height: `${loaderSize.value}px`,
  }
})

const strokeWidth = computed(() => {
  if (!props.size) return 2
  if (props.size < 30) {
    return 4
  }
  return 2
})

const strokeColor = computed(() => {
  if (props.variation === 'snackbar') return 'inherit'

  switch (props.color) {
    case 'black':
      return 'black'
    case 'white':
      return 'white'
    case 'purple':
      return tailwindFlattenedColors['grape-500']
    case 'yellow':
      return tailwindFlattenedColors['canary-500']
    default:
      return 'black'
  }
})

const isLastFrameAnimationFinished = ref(false)
const hasReachedFullProgress = computed(() => {
  return props.progress != null && props.progress === 1
})

// When transforming from spinner to checkmark, we need to wait for the last frame of the spinner animation to finish
// And then we can start the checkmark animation
function endAnimation(): void {
  isLastFrameAnimationFinished.value = true
  emit('transformed-to-checkmark')
}

const circlePath = ref<SVGPathElement | null>(null)
watch(
  () => props.isCheckmark,
  (newValue) => {
    if (!newValue) {
      return
    }
    isLastFrameAnimationFinished.value = false
    if (props.progress !== null || hasReachedFullProgress.value) {
      endAnimation()
      return
    }
    nextTick(() => {
      if (!circlePath.value) {
        endAnimation()
        return
      }
      const listener = () => {
        endAnimation()
        circlePath.value?.removeEventListener('animationiteration', listener)
      }
      circlePath.value?.addEventListener('animationiteration', listener)
    })
  },
  { immediate: true, flush: 'post' },
)
</script>

<template>
  <div
    :class="[
      'relative',
      {
        'color-rainbow': props.color === 'rainbow',
        'color-black': props.color === 'black',
        'color-white': props.color === 'white',
        'color-yellow': props.color === 'yellow',
        'color-purple': props.color === 'purple',
      },
      {
        'color-checkmark-rainbow': effectiveCheckmarkColor === 'rainbow',
        'color-checkmark-black': effectiveCheckmarkColor === 'black',
        'color-checkmark-white': effectiveCheckmarkColor === 'white',
        'color-checkmark-yellow': effectiveCheckmarkColor === 'yellow',
        'color-checkmark-purple': effectiveCheckmarkColor === 'purple',
        'color-checkmark-transparent-with-green-bg': effectiveCheckmarkColor === 'transparent-with-green-bg',
        'color-checkmark-transparent-with-red-bg': effectiveCheckmarkColor === 'transparent-with-red-bg',
        'color-checkmark-transparent-with-yellow-bg': effectiveCheckmarkColor === 'transparent-with-yellow-bg',
        'color-checkmark-transparent-with-purple-bg': effectiveCheckmarkColor === 'transparent-with-purple-bg',
        'color-checkmark-transparent-with-yellow-bg dark:color-checkmark-transparent-with-purple-bg':
          variation === 'snackbar' && darkMode === 'auto',
      },
    ]"
  >
    <div class="sr-only" aria-live="polite">
      <p v-if="!isCheckmark">{{ spinnerLabel }}</p>
      <p v-if="progress !== null">{{ progress * 100 }}%</p>
      <p v-if="isCheckmark">{{ checkmarkLabel }}</p>
    </div>

    <!-- Background path -->
    <svg
      :class="{
        absolute: true,
        'inset-0 w-full h-full': true,
        '-rotate-90': true,
      }"
      viewBox="25 25 50 50"
      aria-hidden="true"
    >
      <circle
        :class="{ backgroundPath: true, complete: isCheckmark && isLastFrameAnimationFinished }"
        cx="50"
        cy="50"
        r="20"
        fill="none"
        :stroke-width="strokeWidth"
        stroke-miterlimit="10"
      ></circle>
    </svg>
    <div :class="['loader']" :style="loaderStyle">
      <!-- Spinner path -->
      <svg
        v-show="props.progress === null || hasReachedFullProgress"
        :class="{
          absolute: true,
          'inset-0 w-full h-full': true,
          rotating: !isCheckmark || !isLastFrameAnimationFinished,
          // Once the checkmark appears, this should not be visible
          hidden: isCheckmark && isLastFrameAnimationFinished,
          'stroke-black': variation === 'snackbar' && darkMode === true,
          'stroke-white': variation === 'snackbar' && darkMode === false,
          'stroke-white dark:stroke-black': variation === 'snackbar' && darkMode === 'auto',
        }"
        viewBox="25 25 50 50"
        aria-hidden="true"
      >
        <circle
          ref="circlePath"
          :class="{ path: true, complete: isCheckmark && isLastFrameAnimationFinished }"
          cx="50"
          cy="50"
          r="20"
          fill="none"
          :stroke="strokeColor"
          :stroke-width="strokeWidth"
          stroke-miterlimit="10"
        ></circle>
      </svg>

      <svg
        v-if="props.progress !== null && !hasReachedFullProgress"
        :class="{
          absolute: true,
          'inset-0 w-full h-full': true,
          '-rotate-90': true,
          '-scale-y-100': true,
          'stroke-black': variation === 'snackbar' && darkMode === true,
          'stroke-white': variation === 'snackbar' && darkMode === false,
          'stroke-white dark:stroke-black': variation === 'snackbar' && darkMode === 'auto',
        }"
        viewBox="25 25 50 50"
        aria-hidden="true"
      >
        <!-- Spinner rail -->
        <circle
          v-if="xSpinnerRail"
          class="transition-all duration-100 ease-in-out motion-reduce:transition-none spinner-rail"
          cx="50"
          cy="50"
          r="20"
          fill="none"
          :stroke-width="strokeWidth"
          stroke-dasharray="150"
          stroke-linecap="round"
          stroke-miterlimit="10"
        ></circle>
        <!-- Spinner progress bar -->
        <circle
          v-if="progress != null"
          class="transition-all duration-100 ease-in-out motion-reduce:transition-none"
          cx="50"
          cy="50"
          r="20"
          fill="none"
          :stroke="strokeColor"
          :stroke-width="strokeWidth"
          stroke-dasharray="150"
          stroke-linecap="round"
          :stroke-dashoffset="-150 * (1 - progress)"
          stroke-miterlimit="10"
        ></circle>
      </svg>
    </div>

    <!-- Checkmark path -->
    <svg
      v-if="
        checkmarkColor !== 'transparent-with-green-bg' &&
        checkmarkColor !== 'transparent-with-red-bg' &&
        variation !== 'snackbar'
      "
      :class="{
        absolute: true,
        'inset-0 w-full h-full': true,
        check: true,
        complete: isCheckmark && isLastFrameAnimationFinished,
      }"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M2 6.75L4.25 9L9.75 3.5" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    <svg
      v-else-if="checkmarkColor === 'transparent-with-green-bg' || checkmarkColor === 'transparent-with-red-bg'"
      :class="{
        absolute: true,
        'inset-0 w-full h-full': true,
        check: true,
        complete: isCheckmark && isLastFrameAnimationFinished,
      }"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        v-if="checkmarkIcon === 'checkmark'"
        class="fill-current"
        d="M10 0C4.486 0 0 4.486 0 10C0 15.514 4.486 20 10 20C15.514 20 20 15.514 20 10C20 4.486 15.514 0 10 0ZM14.2805 8.2805L9.2805 13.2805C9.134 13.427 8.942 13.5 8.75 13.5C8.558 13.5 8.366 13.427 8.2195 13.2805L5.7195 10.7805C5.4265 10.4875 5.4265 10.013 5.7195 9.72C6.0125 9.427 6.487 9.427 6.78 9.72L8.7495 11.6895L13.219 7.22C13.512 6.927 13.9865 6.927 14.2795 7.22C14.5725 7.513 14.573 7.9875 14.2805 8.2805Z"
      />
      <path
        v-else-if="checkmarkIcon === 'error'"
        class="fill-current"
        d="M10 0C4.477 0 0 4.477 0 10C0 15.523 4.477 20 10 20C15.523 20 20 15.523 20 10C20 4.477 15.523 0 10 0ZM13.7805 12.7195C14.0735 13.0125 14.0735 13.487 13.7805 13.78C13.634 13.927 13.442 14 13.25 14C13.058 14 12.866 13.927 12.7195 13.7805L10 11.0605L7.2805 13.78C7.134 13.927 6.942 14 6.75 14C6.558 14 6.366 13.927 6.2195 13.7805C5.9265 13.4875 5.9265 13.013 6.2195 12.72L8.9395 10L6.22 7.2805C5.927 6.9875 5.927 6.513 6.22 6.22C6.513 5.927 6.9875 5.927 7.2805 6.22L10 8.9395L12.7195 6.22C13.0125 5.927 13.487 5.927 13.78 6.22C14.073 6.513 14.073 6.9875 13.78 7.2805L11.0605 10L13.7805 12.7195Z"
      />
    </svg>
    <svg
      v-else-if="variation === 'snackbar'"
      :class="{
        absolute: true,
        'inset-0 w-full h-full': true,
        check: true,
        complete: isCheckmark && isLastFrameAnimationFinished,
      }"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="10" cy="10" r="10" class="fill-current" />
      <path
        d="M8.25 12.2929L6.35355 10.3964C6.15829 10.2012 5.84171 10.2012 5.64645 10.3964C5.45118 10.5917 5.45118 10.9083 5.64645 11.1036L7.89645 13.3536C8.09171 13.5488 8.40829 13.5488 8.60355 13.3536L14.1036 7.85355C14.2988 7.65829 14.2988 7.34171 14.1036 7.14645C13.9083 6.95118 13.5917 6.95118 13.3964 7.14645L8.25 12.2929Z"
        class="checkmark-path"
      />
    </svg>
  </div>
</template>

<style lang="scss" scoped>
$width: 48px;
$small-width: 32px;

.loader {
  position: relative;
  margin: 0px auto;
  width: $width;
}

.loader.is-small,
.loader[data-size='small'] {
  width: $small-width;
}

.loader[data-size='24'] {
  width: 24px;
}

.rotating {
  animation: rotate 2s linear infinite;
  transform-origin: center center;
}
.rotating.complete {
  animation-iteration-count: 1;
}

.backgroundPath {
  stroke: transparent;
  stroke-dasharray: 150;
  stroke-dashoffset: 0;
  stroke-linecap: round;
}
.backgroundPath.complete {
  animation: backgroundPathStrokeDisappear 0.15s ease-in forwards;
}

.path {
  stroke-dasharray: 1, 200;
  stroke-dashoffset: 0;
  animation: dash 1.5s ease-in-out infinite;
  stroke-linecap: round;
  .color-white & {
    stroke: white;
  }
  .color-black & {
    stroke: black;
  }
  .color-purple & {
    stroke: #9466e8;
  }
  .color-yellow & {
    stroke: #fec006;
  }
}
.path.complete {
  animation-iteration-count: 1;
  stroke-dasharray: 89, 200;
  stroke-dashoffset: -200px;
}

.check {
  width: 100%;
  height: 100%;
  display: none;
  .color-checkmark-white & {
    stroke: white;
  }
  .color-checkmark-black & {
    stroke: black;
  }
  .color-checkmark-purple & {
    stroke: #9466e8;
  }
  .color-checkmark-yellow & {
    stroke: #fec006;
  }
  stroke-width: 1px;
  transform-origin: 50% 50%;
  stroke-dasharray: 24;
  stroke-dashoffset: 24;
  stroke-linecap: round;
  animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) forwards;
  .color-checkmark-transparent-with-green-bg & {
    color: #3dc731; //park-500
    animation: fade-in 0.3s ease-in-out forwards;
  }
  .color-checkmark-transparent-with-red-bg & {
    color: #f44336; //scarlet-500
    animation: fade-in 0.3s ease-in-out forwards;
  }
  .color-checkmark-transparent-with-yellow-bg & {
    color: rgba(29, 29, 29, 0.2); //canary-600
    animation: fade-in 0.3s ease-in-out forwards;
  }
  .color-checkmark-transparent-with-purple-bg & {
    color: rgba(29, 29, 29, 0.2);
    animation: fade-in 0.3s ease-in-out forwards;
  }
}
.check.complete {
  display: block;
}

.spinner-rail {
  stroke: rgba(0, 0, 0, 0.38);
}

.checkmark-path {
  fill: white;

  @media (prefers-color-scheme: dark) {
    fill: black;
  }
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -35px;
  }
  100% {
    stroke-dasharray: 89, 200;
    stroke-dashoffset: -124px;
  }
}

@keyframes stroke {
  0% {
    stroke-dashoffset: 24px;
  }
  100% {
    stroke-dashoffset: 0;
  }
}

@keyframes backgroundPathStrokeDisappear {
  0% {
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dashoffset: 150;
  }
}

@keyframes fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
